import { skipToken } from '@reduxjs/toolkit/query'
import { useMemo, useState, type FC } from 'react'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { CustomPaginationComponent } from '../../../components/DataTable/CustomPaginationComponent'
import { Spinner } from '../../../components/Spinner/Spinner'
import { BookingActor, type ArchivedBooking, type SchedulingFilters } from '../../../models/Scheduling'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'
import { useGetArchivedBookingsQuery, useGetSchedulingEventTypesQuery, useGetSchedulingLocationsQuery } from '../../../store/scheduling/scheduling'
import { useGetUsersQuery } from '../../../store/user/user'
import { toBookingsFilterArgs } from '../bookingTabs'
import { HistoryRow } from '../history/HistoryRow'
import { SchedulingEmptyState } from './SchedulingEmptyState'
import { SchedulingTabErrorCard } from './SchedulingTabErrorCard'

export const HistoryTab: FC<{ filters: SchedulingFilters }> = ({ filters }) => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const {
    data: bookingsResponse,
    isFetching,
    isError,
    refetch,
  } = useGetArchivedBookingsQuery({
    ...toBookingsFilterArgs(filters),
    accountId: authenticatedUser.accountId,
    pageRequest: { page: page - 1, size: pageSize },
  })
  const bookings = bookingsResponse?.data
  const totalElements = bookingsResponse?.page.totalElements ?? 0
  const { data: eventTypes } = useGetSchedulingEventTypesQuery({ accountId: authenticatedUser.accountId })
  const { data: locations } = useGetSchedulingLocationsQuery({ accountId: authenticatedUser.accountId })

  // One batched lookup for every user who closed a booking on this page; the current user
  // resolves locally and client actors resolve from each booking's own clients list.
  const closerUserIds = useMemo(() => {
    const ids = new Set<string>()
    for (const booking of bookings ?? []) {
      if (booking.closedBy?.actor === BookingActor.USER && booking.closedBy.id && booking.closedBy.id !== authenticatedUser.id) {
        ids.add(booking.closedBy.id)
      }
    }
    return [...ids]
  }, [bookings, authenticatedUser.id])
  const { data: closerUsersResponse } = useGetUsersQuery(
    closerUserIds.length > 0 ? { accountId: authenticatedUser.accountId, ids: closerUserIds, pageRequest: { page: 0, size: closerUserIds.length } } : skipToken,
  )
  const userNameById = useMemo(() => new Map((closerUsersResponse?.data ?? []).map((user) => [user.id, getUserDisplayName(user)])), [closerUsersResponse])
  const resolveUserName = (userId: string): string | undefined =>
    userId === authenticatedUser.id ? getUserDisplayName(authenticatedUser) : userNameById.get(userId)

  if (isFetching) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  // The archive query is not part of useSchedulingPageStatus at all, so even a first-page
  // failure has to be surfaced here.
  if (isError) {
    return <SchedulingTabErrorCard title="Couldn't load History" onRetry={refetch} />
  }

  if (!Array.isArray(bookings) || bookings.length === 0) {
    return <SchedulingEmptyState title='Nothing archived yet' subtitle='Canceled, declined, expired and archived bookings appear here.' />
  }

  const eventTypeById = new Map((eventTypes ?? []).map((eventType) => [eventType.id, eventType]))
  const locationById = new Map((locations ?? []).map((location) => [location.id, location]))

  return (
    <div className='d-flex flex-column gap-2'>
      {bookings.map((booking: ArchivedBooking) => (
        <HistoryRow
          key={booking.bookingId}
          booking={booking}
          eventType={booking.eventTypeId ? eventTypeById.get(booking.eventTypeId) : undefined}
          location={booking.location?.id ? locationById.get(booking.location.id) : undefined}
          currentUserId={authenticatedUser.id}
          resolveUserName={resolveUserName}
        />
      ))}
      <CustomPaginationComponent
        rowsPerPage={pageSize}
        rowCount={totalElements}
        currentPage={page}
        onChangePage={setPage}
        onChangeRowsPerPage={(newPageSize) => {
          setPage(1)
          setPageSize(newPageSize)
        }}
      />
    </div>
  )
}
